import { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Header from "../components/Common/Header";
import {
  LocationUpdate,
  getLocation,
  updateLocation,
  deleteLocation,
  untagItemFromLocation,
  tagItemToLocation,
} from "../api/locations";
import { useMediaUrl } from "../hooks/useMediaUrl";
import { PersonPicker } from "../components/Person";
import { MediaPicker } from "../components/Media";

// Component to display a media thumbnail with auth
function MediaThumbnail({ mediaId, filename }: { mediaId: number; filename: string }) {
  const { url, isLoading } = useMediaUrl(mediaId);
  
  if (isLoading) {
    return (
      <div className="h-20 w-20 bg-vault-100 rounded animate-pulse" />
    );
  }
  
  return (
    <img
      src={url || ""}
      alt={filename}
      className="h-20 w-20 object-cover rounded"
    />
  );
}

export default function LocationDetail() {
  const { locationId } = useParams<{ locationId: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAddPeople, setShowAddPeople] = useState(false);
  const [showAddMedia, setShowAddMedia] = useState(false);
  const [editData, setEditData] = useState<LocationUpdate>({});

  // Fetch location details
  const { data: location, isLoading, error } = useQuery({
    queryKey: ["location", locationId],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getLocation(token, parseInt(locationId!));
    },
    enabled: !!locationId,
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: LocationUpdate) => {
      const token = await getAccessTokenSilently();
      return updateLocation(token, parseInt(locationId!), data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["location", locationId] });
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      setIsEditing(false);
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      const token = await getAccessTokenSilently();
      return deleteLocation(token, parseInt(locationId!));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
      navigate("/locations");
    },
  });

  // Untag mutation
  const untagMutation = useMutation({
    mutationFn: async (params: { mediaId?: number; personId?: number; connectedPersonId?: number }) => {
      const token = await getAccessTokenSilently();
      return untagItemFromLocation(token, parseInt(locationId!), {
        media_id: params.mediaId,
        person_id: params.personId,
        connected_person_id: params.connectedPersonId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["location", locationId] });
    },
  });

  // Tag mutation
  const tagMutation = useMutation({
    mutationFn: async (params: { personId?: number; connectedPersonId?: number; mediaId?: number }) => {
      const token = await getAccessTokenSilently();
      return tagItemToLocation(token, parseInt(locationId!), {
        person_id: params.personId,
        connected_person_id: params.connectedPersonId,
        media_id: params.mediaId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["location", locationId] });
    },
  });

  const startEditing = () => {
    if (location) {
      setEditData({
        name: location.name,
        description: location.description || "",
        address: location.address || "",
        city: location.city || "",
        county: location.county || "",
        country: location.country || "",
        postal_code: location.postal_code || "",
        geo_lat: location.geo_lat || undefined,
        geo_lng: location.geo_lng || undefined,
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-vault-300 border-t-vault-600" />
          </div>
        </main>
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-4xl px-4 py-8">
          <div className="rounded-lg bg-red-50 p-4 text-red-700">
            Failed to load location. It may have been deleted.
          </div>
          <Link to="/locations" className="mt-4 inline-block text-vault-600 hover:underline">
            ← Back to Locations
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />

      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <nav className="mb-4">
          <Link to="/locations" className="text-sm text-vault-600 hover:text-vault-900">
            ← Back to Locations
          </Link>
        </nav>

        {/* Header */}
        <div className="mb-6 flex items-start justify-between">
          <div className="flex items-center gap-4">
            <div className="h-14 w-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="font-serif text-3xl font-bold text-vault-900">{location.name}</h1>
              <p className="text-vault-600">{location.display_address}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={startEditing}
              className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
            >
              Edit
            </button>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-lg bg-red-100 px-4 py-2 text-red-700 hover:bg-red-200"
            >
              Delete
            </button>
          </div>
        </div>

        {/* Location Details Card */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-vault-900">Details</h2>
          
          {location.description && (
            <p className="mb-4 text-vault-700">{location.description}</p>
          )}
          
          <dl className="grid grid-cols-2 gap-4 text-sm">
            {location.address && (
              <div>
                <dt className="text-vault-500">Address</dt>
                <dd className="text-vault-900">{location.address}</dd>
              </div>
            )}
            {location.city && (
              <div>
                <dt className="text-vault-500">City/Town</dt>
                <dd className="text-vault-900">{location.city}</dd>
              </div>
            )}
            {location.county && (
              <div>
                <dt className="text-vault-500">County/Region</dt>
                <dd className="text-vault-900">{location.county}</dd>
              </div>
            )}
            {location.country && (
              <div>
                <dt className="text-vault-500">Country</dt>
                <dd className="text-vault-900">{location.country}</dd>
              </div>
            )}
            {location.postal_code && (
              <div>
                <dt className="text-vault-500">Postal Code</dt>
                <dd className="text-vault-900">{location.postal_code}</dd>
              </div>
            )}
            {location.geo_lat && location.geo_lng && (
              <div>
                <dt className="text-vault-500">Coordinates</dt>
                <dd className="text-vault-900">
                  {location.geo_lat.toFixed(6)}, {location.geo_lng.toFixed(6)}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Tagged Media */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg font-semibold text-vault-900">
              Tagged Media ({location.media_items.length})
            </h2>
            <button
              onClick={() => setShowAddMedia(!showAddMedia)}
              className="text-sm text-vault-600 hover:text-vault-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add Media
            </button>
          </div>

          {/* Add Media Picker */}
          {showAddMedia && (
            <div className="mb-4 p-4 bg-vault-50 rounded-lg">
              {tagMutation.error && (
                <div className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">
                  {tagMutation.error instanceof Error ? tagMutation.error.message : "Failed to tag media"}
                </div>
              )}
              <MediaPicker
                selectedMediaIds={location.media_items.map((m) => m.id)}
                onAddMedia={(id) => tagMutation.mutate({ mediaId: id })}
                onRemoveMedia={(id) => untagMutation.mutate({ mediaId: id })}
                placeholder="Search for media to add..."
              />
              <button
                onClick={() => setShowAddMedia(false)}
                className="mt-2 text-sm text-vault-600 hover:text-vault-900"
              >
                Done adding media
              </button>
            </div>
          )}
          
          {location.media_items.length === 0 ? (
            <p className="text-vault-500">No media tagged to this location yet.</p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-3">
              {location.media_items.map((media) => (
                <div key={media.id} className="group relative">
                  <MediaThumbnail mediaId={media.id} filename={media.filename} />
                  <button
                    onClick={() => untagMutation.mutate({ mediaId: media.id })}
                    className="absolute -top-1 -right-1 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-xs"
                    title="Remove tag"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Tagged People */}
        <div className="mb-6 rounded-lg bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg font-semibold text-vault-900">
              Tagged People ({location.persons.length + location.connected_persons.length})
            </h2>
            <button
              onClick={() => setShowAddPeople(!showAddPeople)}
              className="text-sm text-vault-600 hover:text-vault-900 flex items-center gap-1"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add People
            </button>
          </div>

          {/* Add People Picker */}
          {showAddPeople && (
            <div className="mb-4 p-4 bg-vault-50 rounded-lg">
              {tagMutation.error && (
                <div className="mb-3 rounded bg-red-50 p-3 text-sm text-red-700">
                  {tagMutation.error instanceof Error ? tagMutation.error.message : "Failed to tag person"}
                </div>
              )}
              <PersonPicker
                selectedPersonIds={location.persons.map((p) => p.id)}
                selectedConnectedPersonIds={location.connected_persons.map((p) => p.id)}
                onAddPerson={(id) => tagMutation.mutate({ personId: id })}
                onAddConnectedPerson={(id) => tagMutation.mutate({ connectedPersonId: id })}
                onRemovePerson={(id) => untagMutation.mutate({ personId: id })}
                onRemoveConnectedPerson={(id) => untagMutation.mutate({ connectedPersonId: id })}
                placeholder="Search for people to add..."
              />
              <button
                onClick={() => setShowAddPeople(false)}
                className="mt-2 text-sm text-vault-600 hover:text-vault-900"
              >
                Done adding people
              </button>
            </div>
          )}
          
          {location.persons.length === 0 && location.connected_persons.length === 0 ? (
            <p className="text-vault-500">No people tagged to this location yet.</p>
          ) : (
            <div className="space-y-2">
              {location.persons.map((person) => (
                <div key={`person-${person.id}`} className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-vault-900">{person.display_name}</span>
                    <span className="text-xs text-vault-500 bg-vault-100 px-2 py-0.5 rounded">Family</span>
                  </div>
                  <button
                    onClick={() => untagMutation.mutate({ personId: person.id })}
                    className="text-vault-400 hover:text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
              {location.connected_persons.map((person) => (
                <div key={`cp-${person.id}`} className="flex items-center justify-between py-2 border-b border-vault-100 last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-vault-900">{person.display_name}</span>
                    <span className="text-xs text-vault-500 bg-amber-100 px-2 py-0.5 rounded">Connected</span>
                  </div>
                  <button
                    onClick={() => untagMutation.mutate({ connectedPersonId: person.id })}
                    className="text-vault-400 hover:text-red-600 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Edit Modal */}
      {isEditing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
            <h2 className="mb-4 text-xl font-semibold text-vault-900">Edit Location</h2>

            {updateMutation.error && (
              <div className="mb-4 rounded bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error instanceof Error ? updateMutation.error.message : "Failed to update"}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-vault-700">Name</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={editData.name || ""}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Description</label>
                <textarea
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  rows={2}
                  value={editData.description || ""}
                  onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-vault-700">Address</label>
                <input
                  type="text"
                  className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                  value={editData.address || ""}
                  onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">City/Town</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editData.city || ""}
                    onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">County/Region</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editData.county || ""}
                    onChange={(e) => setEditData({ ...editData, county: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-vault-700">Country</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editData.country || ""}
                    onChange={(e) => setEditData({ ...editData, country: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-vault-700">Postal Code</label>
                  <input
                    type="text"
                    className="mt-1 w-full rounded border border-vault-200 px-3 py-2"
                    value={editData.postal_code || ""}
                    onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setIsEditing(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={updateMutation.isPending}
                className="rounded-lg bg-vault-600 px-4 py-2 text-white hover:bg-vault-700 disabled:opacity-50"
              >
                {updateMutation.isPending ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-semibold text-vault-900">Delete Location?</h2>
            <p className="mb-4 text-vault-600">
              Are you sure you want to delete <span className="font-medium">{location.name}</span>?
              This will remove all tags to this location.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg bg-vault-100 px-4 py-2 text-vault-700 hover:bg-vault-200"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
